import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Divider,
  Collapse,
  Link,
  alpha,
  useTheme,
  IconButton,
} from "@mui/material";
import {
  Phone,
  CallEnd,
  VolumeOff,
  Remove,
  OpenInFull,
  CallReceived,
  Person,
  ExpandMore,
  ExpandLess,
  OpenInNew,
  Business,
  Email,
} from "@mui/icons-material";
import { formatPhone } from "../../../utils/phoneUtils";
import { keyframes } from "@mui/system";
import { openExternal } from "../../../utils/openExternal";
import { useSelector } from "react-redux";
import { getCrmMeta } from "../../../utils/crmUtils";
import { selectCrmCatalog } from "../../../state/integration/integrationSlice";

const ringingAnimation = keyframes`
  0% { transform: rotate(0deg); }
  5% { transform: rotate(30deg); }
  10% { transform: rotate(-28deg); }
  15% { transform: rotate(34deg); }
  20% { transform: rotate(-32deg); }
  25% { transform: rotate(30deg); }
  30% { transform: rotate(-28deg); }
  35% { transform: rotate(26deg); }
  40% { transform: rotate(-24deg); }
  45% { transform: rotate(22deg); }
  50% { transform: rotate(-20deg); }
  55% { transform: rotate(18deg); }
  60% { transform: rotate(-16deg); }
  65% { transform: rotate(14deg); }
  70% { transform: rotate(-12deg); }
  75% { transform: rotate(10deg); }
  80% { transform: rotate(-8deg); }
  85% { transform: rotate(6deg); }
  90% { transform: rotate(-4deg); }
  95% { transform: rotate(2deg); }
  100% { transform: rotate(0deg); }
`;

const IncomingCallCard = ({
  callSession,
  onAnswer,
  onIgnore,
  onReject,
  onMinimize,
  isMinimized,
  crmContact,
  crmType,
}) => {
  const theme = useTheme();
  const crmCatalog = useSelector(selectCrmCatalog);
  const crm = crmContact ? getCrmMeta(crmType, crmCatalog[crmType]) : null;
  const [crmExpanded, setCrmExpanded] = useState(false);

  const iconStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderRadius: "50%",
    },
  };

  const buttonStyle = {
    color: theme.palette.common.white,
    borderRadius: 2,
    "& .MuiSvgIcon-root": {
      color: theme.palette.common.white,
    },
  };

  const animatedIconStyle = {
    animation: `${ringingAnimation} 2s infinite`,
    transformOrigin: "center",
    color: theme.palette.secondary.main,
  };

  return (
    <Card
      sx={{
        width: isMinimized ? "250px" : "380px",
        borderRadius: 4,
        backgroundColor: alpha(theme.palette.background.paper, 0.4),
        backdropFilter: "blur(8px)",
        color: theme.palette.primary.contrastText,
        position: "relative",
      }}
    >
      <CardContent>
        {isMinimized ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Phone color="info" sx={{ ...animatedIconStyle, fontSize: 24 }} />
            <Typography variant="subtitle1" sx={{ mx: 2, flexGrow: 1 }}>
              {callSession.displayName || formatPhone(callSession.number)}
            </Typography>
            <IconButton onClick={onMinimize} size="small" sx={iconStyle}>
              <OpenInFull fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <React.Fragment>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CallReceived color="info" sx={{ mr: 2 }} />
                <Typography variant="h6" component="div">
                  Incoming Call
                </Typography>
              </Box>
              <IconButton onClick={onMinimize} sx={iconStyle}>
                <Remove />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
              <Person sx={{ mr: 2, fontSize: 60 }} />
              <Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {callSession.displayName || formatPhone(callSession.number)}
                </Typography>
                <Typography variant="body2">
                  {formatPhone(callSession.number)}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-around", mt: 2 }}
            >
              <Button
                variant="contained"
                color="success"
                startIcon={<Phone />}
                onClick={() => onAnswer(callSession)}
                sx={buttonStyle}
              >
                Answer
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<VolumeOff />}
                onClick={() => onIgnore(callSession)}
                sx={buttonStyle}
              >
                Ignore
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CallEnd />}
                onClick={() => onReject(callSession)}
                sx={buttonStyle}
              >
                Reject
              </Button>
            </Box>

            {crmContact && crm && (
              <>
                <Divider sx={{ mt: 2, mb: 1.5, borderColor: alpha(theme.palette.common.white, 0.1) }} />

                {/* Compact strip — always visible */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: crm.primary, flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                    {crmContact.name}
                    {crmContact.company && (
                      <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 0.5 }}>
                        · {crmContact.company}
                      </Box>
                    )}
                  </Typography>
                  <Typography variant="caption" sx={{ color: crm.primary, fontWeight: 600, fontSize: "0.65rem", letterSpacing: "0.04em", textTransform: "uppercase", flexShrink: 0 }}>
                    {crm.label}
                  </Typography>
                  {crmContact.profileUrl && (
                    <IconButton
                      size="small"
                      onClick={() => openExternal(crmContact.profileUrl)}
                      sx={{ p: 0.25, color: crm.primary }}
                    >
                      <OpenInNew sx={{ fontSize: 13 }} />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => setCrmExpanded(v => !v)}
                    sx={{ p: 0.25, color: "text.secondary" }}
                  >
                    {crmExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Box>

                {/* Expanded details — styled like CrmContactCard */}
                <Collapse in={crmExpanded}>
                  <Box
                    sx={{
                      mt: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(crm.primary, 0.2)}`,
                      borderTop: `3px solid ${crm.primary}`,
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      backgroundColor: alpha(crm.primary, 0.04),
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>{crmContact.name}</Typography>
                    {crmContact.company && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Business sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">{crmContact.company}</Typography>
                      </Box>
                    )}
                    {crmContact.title && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Person sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">{crmContact.title}</Typography>
                      </Box>
                    )}
                    {crmContact.email && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Email sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">{crmContact.email}</Typography>
                      </Box>
                    )}
                    {crmContact.profileUrl && (
                      <Link
                        component="button"
                        onClick={() => openExternal(crmContact.profileUrl)}
                        sx={{ display: "flex", alignItems: "center", gap: 0.4, fontSize: 11, color: crm.primary, mt: 0.25, width: "fit-content" }}
                      >
                        Open in {crm.label} <OpenInNew sx={{ fontSize: 11 }} />
                      </Link>
                    )}
                  </Box>
                </Collapse>
              </>
            )}
          </React.Fragment>
        )}
      </CardContent>
    </Card>
  );
};

export default IncomingCallCard;
